import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, FileText, Download, User, ChevronDown, BarChart3 } from 'lucide-react';
import api, { API_URL } from '../utils/api';
import ReportPopover from './ReportPopover';
import PasswordChangePopover from './PasswordChangePopover';
import UserManagementPopover from './UserManagementPopover';
import CitizenshipManagementPopover from './CitizenshipManagementPopover';
import CourseManagementPopover from './CourseManagementPopover';
import FinancialStatusManagementPopover from './FinancialStatusManagementPopover';
import SemesterManagementPopover from './SemesterManagementPopover';
import './MainApp.css';

function MainApp() {
  const [user, setUser] = useState(null);
  const [conversions, setConversions] = useState([]);
  const [loadingConversions, setLoadingConversions] = useState(true);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [error, setError] = useState(null);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [selectedReportJobId, setSelectedReportJobId] = useState(null);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [showCitizenshipManagement, setShowCitizenshipManagement] = useState(false);
  const [showCourseManagement, setShowCourseManagement] = useState(false);
  const [showFinancialStatusManagement, setShowFinancialStatusManagement] = useState(false);
  const [showSemesterManagement, setShowSemesterManagement] = useState(false);
  const [aiSplitLoading, setAiSplitLoading] = useState(false);
  // Maps jobId -> boolean: whether to use AI addresses for downloads (default true when AI is done)
  const [aiUseMap, setAiUseMap] = useState({});
  // Set of job IDs that should show the new-conversion glow animation
  const [newJobIds, setNewJobIds] = useState(new Set());
  // Maps jobId -> boolean: whether to download only new (deduplicated) records
  const [dedupMap, setDedupMap] = useState({});

  // Fetch conversion results from backend
  const fetchConversions = React.useCallback(async () => {
    try {
      const response = await api.get('/jobs');
      setConversions(response.data.jobs || []);
      setLoadingConversions(false);
    } catch (err) {
      console.error('Failed to fetch conversions:', err);
      setLoadingConversions(false);
    }
  }, []);

  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];
    setLoading(true);
    setError(null);
    setUploadedFile({ filename: file.name });

    try {
      const formData = new FormData();
      formData.append('file', file);

      // Upload and convert — response is discarded, we fetch the job list instead
      await api.post(
        '/api/upload-and-convert',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          responseType: 'blob' // avoid axios JSON parse error on binary response
        }
      );

      setUploadedFile(null);
      setLoading(false);

      // Refresh conversions list and highlight the newest completed job
      try {
        const jobsResponse = await api.get('/jobs');
        const jobs = jobsResponse.data.jobs || [];
        setConversions(prev => {
          const prevIds = new Set(prev.map(j => j.id));
          const freshIds = jobs
            .filter(j => j.status === 'completed' && !prevIds.has(j.id))
            .map(j => j.id);
          if (freshIds.length > 0) {
            setNewJobIds(ids => {
              const next = new Set([...ids, ...freshIds]);
              // Remove glow after animation completes
              setTimeout(() => {
                setNewJobIds(cur => {
                  const cleaned = new Set(cur);
                  freshIds.forEach(id => cleaned.delete(id));
                  return cleaned;
                });
              }, 3000);
              return next;
            });
          }
          return jobs;
        });
      } catch (refreshErr) {
        console.error('Failed to refresh conversions:', refreshErr);
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.response?.data?.detail || 'Feltöltés és konvertálás sikertelen');
      setLoading(false);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
    multiple: false,
  });

  const handleDownload = async (jobId, useAi = false) => {
    try {
      const response = await api.get(`/download/${jobId}`, {
        params: { use_ai: useAi },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'converted_file.csv';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
        if (filenameMatch) filename = filenameMatch[1];
      }
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
      alert('Letöltés sikertelen');
    }
  };

  const handleAiAddressSplit = async (jobId) => {
    setAiSplitLoading(true);
    try {
      await api.post(`/ai-address-split/${jobId}`, {});
      // Default the checkbox to checked for this job
      setAiUseMap(prev => ({ ...prev, [jobId]: true }));
      // Refresh job list so ai_address_split_at is reflected
      const jobsResponse = await api.get('/jobs');
      setConversions(jobsResponse.data.jobs || []);
    } catch (err) {
      console.error('AI address split error:', err);
      const message = err.response?.data?.detail
        ? `AI cím feldolgozás sikertelen: ${err.response.data.detail}`
        : 'AI cím feldolgozás sikertelen';
      alert(message);
    } finally {
      setAiSplitLoading(false);
    }
  };

  const handleDeduplicatedDownload = async (jobId, useAi = false) => {
    try {
      const response = await api.get(`/download-dedup/${jobId}`, {
        params: { use_ai: useAi },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'deduplicated_file.xlsx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
        if (filenameMatch) filename = filenameMatch[1];
      }
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Dedup download error:', err);
      alert('Deduplikált letöltés sikertelen');
    }
  };

  const handlePasswordChange = () => {
    setShowProfileMenu(false);
    setShowPasswordChange(true);
  };

  const handleUserManagement = () => {
    setShowProfileMenu(false);
    setShowUserManagement(true);
  };

  const handleCitizenshipManagement = () => {
    setShowProfileMenu(false);
    setShowCitizenshipManagement(true);
  };

  const handleCourseManagement = () => {
    setShowProfileMenu(false);
    setShowCourseManagement(true);
  };

  const handleFinancialStatusManagement = () => {
    setShowProfileMenu(false);
    setShowFinancialStatusManagement(true);
  };

  const handleSemesterManagement = () => {
    setShowProfileMenu(false);
    setShowSemesterManagement(true);
  };

  const handlePasswordChangeSuccess = () => {
    // Optionally show a success notification
    console.log('Password changed successfully');
  };

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('sessionToken');
      if (token) {
        // Call backend logout endpoint
        await api.post('/api/auth/logout', {});
      }
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      // Clear local storage and redirect
      localStorage.removeItem('sessionToken');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
  };

  // Load user from localStorage
  React.useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  // Load conversions on mount
  React.useEffect(() => {
    fetchConversions();
  }, [fetchConversions]);

  // Close profile menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (showProfileMenu && !event.target.closest('.profile-section')) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showProfileMenu]);

  return (
    <div className="MainApp">
      {/* AI Address Split loading spinner */}
      {aiSplitLoading && (
        <div className="ai-address-modal-overlay">
          <div className="ai-address-modal">
            <div className="ai-spinner" />
            <p>AI Címbontás folyamatban...<br />Ez eltarthat néhány percig.</p>
          </div>
        </div>
      )}
      {/* Header */}
      <header className="app-header">
        <div className="header-content">
          <h1 className="app-logo">Il Corvo</h1>
          <div className="profile-section">
            <button 
              className="profile-button"
              onClick={() => setShowProfileMenu(!showProfileMenu)}
            >
              <User size={18} />
              <span>{user?.full_name || 'User'}</span>
              <ChevronDown size={16} />
            </button>
            {showProfileMenu && (
              <div className="profile-menu">
                <button onClick={handlePasswordChange}>Jelszómódosítás</button>
                {user?.role === 'admin' && (
                  <>
                    <button onClick={handleUserManagement}>Felhasználókezelés</button>
                    <button onClick={handleCitizenshipManagement}>Állampolgárság-kezelés</button>
                    <button onClick={handleCourseManagement}>Képzések</button>
                    <button onClick={handleFinancialStatusManagement}>Pénzügyi státuszok</button>
                    <button onClick={handleSemesterManagement}>Félévek</button>
                  </>
                )}
                <div className="profile-menu-separator" />
                <button onClick={handleLogout}>Kijelentkezés</button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="main-content">
        {/* Upload Section */}
        <div className="upload-card">
          <h2 className="section-title">Új konverzió</h2>
          <p className="section-subtitle">Töltsön fel egy új fájlt konvertálásra</p>
          
          <div className="upload-area">
            <div {...getRootProps()} className={`dropzone-new ${isDragActive ? 'active' : ''}`}>
              <input {...getInputProps()} />
              <div className="upload-prompt">
                {uploadedFile ? (
                  <>
                    <FileText size={20} className="upload-icon" />
                    <span className="file-selected">{uploadedFile.filename}</span>
                    <span className="file-status">nincs kijelölt fájl</span>
                  </>
                ) : (
                  <>
                    <span className="file-label">Fájl kiválasztása</span>
                    <span className="file-status">nincs kijelölt fájl</span>
                  </>
                )}
              </div>
            </div>
            <button 
              className="upload-button" 
              disabled={loading}
              type="button"
            >
              <Upload size={18} />
              {loading ? 'Feldolgozás...' : 'Feltöltés'}
            </button>
          </div>
        </div>

        {/* Conversion Results */}
        <div className="results-section">
          <h2 className="section-title">Konverziós eredmények</h2>
          <p className="section-subtitle">Tekintse meg és töltse le konvertált fájljait</p>

          <div className="conversions-list">
            {loadingConversions ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                Betöltés...
              </div>
            ) : conversions.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                Még nincsenek konverziós eredmények
              </div>
            ) : (
              conversions.map((conversion) => {
                // Format date
                const convertedAt = conversion.processing_completed_at 
                  ? new Date(conversion.processing_completed_at).toLocaleString('hu-HU', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit'
                    })
                  : 'N/A';
                
                // Status translation
                const statusMap = {
                  'completed': 'KÉSZ',
                  'processing': 'FELDOLGOZÁS',
                  'failed': 'HIBA',
                  'pending': 'VÁRAKOZIK'
                };
                const status = statusMap[conversion.status] || conversion.status.toUpperCase();
                
                const aiDone = Boolean(conversion.ai_address_split_at);
                const useAi = aiDone
                  ? (aiUseMap[conversion.id] !== undefined ? aiUseMap[conversion.id] : true)
                  : false;

                return (
                  <div key={conversion.id} className={`conversion-item${newJobIds.has(conversion.id) ? ' conversion-item--new' : ''}`}>
                    <div className="conversion-header">
                      <FileText size={20} className="file-icon" />
                      <div className="conversion-info">
                        <h3 className="conversion-filename">{conversion.filename}</h3>
                        <p className="conversion-date">Konvertálva: {convertedAt}</p>
                      </div>
                      <span className={`status-badge status-${conversion.status}`}>{status}</span>
                    </div>
                    <div className="conversion-actions">
                      <button
                        className="action-btn download-btn"
                        onClick={() =>
                          dedupMap[conversion.id]
                            ? handleDeduplicatedDownload(conversion.id, useAi)
                            : handleDownload(conversion.id, useAi)
                        }
                        disabled={conversion.status !== 'completed'}
                      >
                        <Download size={16} />
                        Letöltés
                      </button>
                      <label className="ai-use-checkbox">
                        <input
                          type="checkbox"
                          checked={dedupMap[conversion.id] ?? false}
                          onChange={e =>
                            setDedupMap(prev => ({ ...prev, [conversion.id]: e.target.checked }))
                          }
                          disabled={conversion.status !== 'completed'}
                        />
                        Csak az új rekordokat tartalmazza
                      </label>
                      <div className="ai-action-slot">
                        {aiDone ? (
                          <label className="ai-use-checkbox">
                            <input
                              type="checkbox"
                              checked={useAi}
                              onChange={e =>
                                setAiUseMap(prev => ({ ...prev, [conversion.id]: e.target.checked }))
                              }
                            />
                            AI címek használata
                          </label>
                        ) : (
                          <button
                            className="action-btn ai-btn"
                            onClick={() => handleAiAddressSplit(conversion.id)}
                            disabled={conversion.status !== 'completed' || aiSplitLoading}
                          >
                            AI Címbontás
                          </button>
                        )}
                      </div>
                      <button
                        className="action-btn report-btn"
                        onClick={() => setSelectedReportJobId(conversion.id)}
                        disabled={conversion.status !== 'completed'}
                      >
                        <BarChart3 size={16} />
                        Konvertálási riport
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Report Popover */}
      {selectedReportJobId && (
        <ReportPopover 
          jobId={selectedReportJobId} 
          onClose={() => setSelectedReportJobId(null)} 
        />
      )}

      {/* Password Change Popover */}
      {showPasswordChange && (
        <PasswordChangePopover 
          onClose={() => setShowPasswordChange(false)}
          onSuccess={handlePasswordChangeSuccess}
        />
      )}

      {/* User Management Popover (admin only) */}
      {showUserManagement && user?.role === 'admin' && (
        <UserManagementPopover 
          onClose={() => setShowUserManagement(false)}
        />
      )}

      {/* Citizenship Management Popover (admin only) */}
      {showCitizenshipManagement && user?.role === 'admin' && (
        <CitizenshipManagementPopover
          onClose={() => setShowCitizenshipManagement(false)}
        />
      )}

      {/* Course Management Popover (admin only) */}
      {showCourseManagement && user?.role === 'admin' && (
        <CourseManagementPopover
          onClose={() => setShowCourseManagement(false)}
        />
      )}

      {/* Financial Status Management Popover (admin only) */}
      {showFinancialStatusManagement && user?.role === 'admin' && (
        <FinancialStatusManagementPopover
          onClose={() => setShowFinancialStatusManagement(false)}
        />
      )}

      {/* Semester Management Popover (admin only) */}
      {showSemesterManagement && user?.role === 'admin' && (
        <SemesterManagementPopover
          onClose={() => setShowSemesterManagement(false)}
        />
      )}
    </div>
  );
}

export default MainApp;
